import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
  People as PeopleIcon,
  Domain as DomainIcon,
  DateRange as DateRangeIcon,
  AccountBalance as AccountBalanceIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as AttachMoneyIcon,
  Receipt as ReceiptIcon,
  AutoMode as AutoModeIcon,
  LocationOn as LocationOnIcon,
  Devices as DevicesIcon,
  Security as SecurityIcon,
  Extension as ExtensionIcon,
} from '@mui/icons-material';
import api from '../api';
import { useTerms } from '../context/TerminologyContext';
import { getTenantSettings } from '../api';
import { useAuth } from '../context/AuthContext';
import { passesRequireAdmin } from '../utils/endpointAccess';

const STEPS = [
  {
    id: 'company',
    title: 'Company Information',
    required: true,
    icon: BusinessIcon,
  },
  {
    id: 'employees',
    title: 'Add Employees',
    required: true,
    icon: PeopleIcon,
  },
  {
    id: 'companies',
    title: 'Add Client Companies',
    required: true,
    icon: DomainIcon,
  },
  {
    id: 'payperiods',
    title: 'Set Up Pay Periods',
    required: true,
    icon: DateRangeIcon,
  },
  {
    id: 'deductions',
    title: 'Configure Deductions',
    required: false,
    icon: AccountBalanceIcon,
  },
  {
    id: 'timetracking',
    title: 'Time Tracking',
    required: false,
    icon: AccessTimeIcon,
  },
  {
    id: 'modules',
    title: 'Modules & Employee Home',
    required: false,
    icon: ExtensionIcon,
  },
  {
    id: 'pipeline',
    title: 'Payment Pipeline',
    required: false,
    icon: TrendingUpIcon,
  },
  {
    id: 'payroll',
    title: 'Running Payroll',
    required: false,
    icon: AttachMoneyIcon,
  },
  {
    id: 'invoiceautomation',
    title: 'Invoice Automation',
    required: false,
    icon: ReceiptIcon,
  },
  {
    id: 'payrollautomation',
    title: 'Payroll Automation',
    required: false,
    icon: AutoModeIcon,
  },
  {
    id: 'worksites',
    title: 'Work Sites & IP Restrictions',
    required: false,
    icon: LocationOnIcon,
  },
  {
    id: 'kiosk',
    title: 'Timeclock Kiosk',
    required: false,
    icon: DevicesIcon,
  },
  {
    id: 'permissions',
    title: 'Permissions & Time Editing',
    required: false,
    icon: SecurityIcon,
  },
];

const REQUIRED_STEP_COUNT = STEPS.filter((s) => s.required).length;

function StatusChip({ status }) {
  if (status === 'complete') {
    return (
      <Chip
        icon={<CheckCircleIcon />}
        label="Complete"
        color="success"
        size="small"
        variant="outlined"
      />
    );
  }
  if (status === 'in-progress') {
    return (
      <Chip
        icon={<WarningIcon />}
        label="In Progress"
        color="warning"
        size="small"
        variant="outlined"
      />
    );
  }
  return (
    <Chip
      icon={<RadioButtonUncheckedIcon />}
      label="Not Started"
      size="small"
      variant="outlined"
    />
  );
}


function BulletList({ items }) {
  return (
    <List dense disablePadding>
      {items.map((item, i) => (
        <ListItem key={i} sx={{ py: 0.25, pl: 2 }}>
          <ListItemIcon sx={{ minWidth: 28 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'text.secondary',
              }}
            />
          </ListItemIcon>
          <ListItemText
            primary={item}
            primaryTypographyProps={{ variant: 'body2' }}
          />
        </ListItem>
      ))}
    </List>
  );
}

function SectionHeading({ label }) {
  return (
    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1.5, mb: 0.5 }}>
      {label}
    </Typography>
  );
}

const MANUAL_STORAGE_KEY = 'hourtrax_setup_manual_complete';

function getManualCompleted() {
  try {
    return JSON.parse(localStorage.getItem(MANUAL_STORAGE_KEY) || '{}');
  } catch { return {}; }
}

function setManualCompleted(data) {
  try { localStorage.setItem(MANUAL_STORAGE_KEY, JSON.stringify(data)); } catch { /* ignore */ }
}

export default function SetupGuide() {
  const { t } = useTerms();
  const { user } = useAuth();
  const canReadSettings = passesRequireAdmin(user);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stepStatuses, setStepStatuses] = useState({});
  const [manualComplete, setManualComplete] = useState(getManualCompleted);

  const toggleManualComplete = (stepId) => {
    setManualComplete(prev => {
      const updated = { ...prev };
      if (updated[stepId]) {
        delete updated[stepId];
      } else {
        updated[stepId] = true;
      }
      setManualCompleted(updated);
      return updated;
    });
  };

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [settingsRes, employeesRes, companiesRes, payPeriodsRes] = await Promise.all([
        canReadSettings ? getTenantSettings().catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        api.get('/employees').catch(() => ({ data: [] })),
        api.get('/companies').catch(() => ({ data: [] })),
        api.get('/pay-periods').catch(() => ({ data: [] })),
      ]);

      const settings = settingsRes.data || {};
      const employees = Array.isArray(employeesRes.data) ? employeesRes.data : [];
      const companies = Array.isArray(companiesRes.data) ? companiesRes.data : [];
      const payPeriods = Array.isArray(payPeriodsRes.data) ? payPeriodsRes.data : [];

      const hasDisplayName = !!(settings.display_name && settings.display_name.trim());
      const hasEin = !!(settings.ein && settings.ein.trim());

      const statuses = {
        company: hasDisplayName && hasEin ? 'complete' : hasDisplayName || hasEin ? 'in-progress' : 'not-started',
        employees: employees.length > 0 ? 'complete' : 'not-started',
        companies: companies.length > 0 ? 'complete' : 'not-started',
        payperiods: payPeriods.length > 0 ? 'complete' : 'not-started',
        deductions: 'not-started',
        timetracking: 'not-started',
        modules: 'not-started',
        pipeline: 'not-started',
        payroll: 'not-started',
        invoiceautomation: 'not-started',
        payrollautomation: 'not-started',
      };

      setStepStatuses(statuses);
    } catch (err) {
      setError('Failed to load setup status. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [canReadSettings]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleAccordionChange = (panel) => (_event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getEffectiveStatus = (stepId) => {
    if (manualComplete[stepId]) return 'complete';
    return stepStatuses[stepId] || 'not-started';
  };

  const completedRequired = STEPS.filter(
    (s) => s.required && getEffectiveStatus(s.id) === 'complete'
  ).length;

  const progressPercent = REQUIRED_STEP_COUNT > 0
    ? Math.round((completedRequired / REQUIRED_STEP_COUNT) * 100)
    : 0;

  const renderStepContent = (stepId) => {
    switch (stepId) {
      case 'company':
        return (
          <Box>
            <SectionHeading label="What to do" />
            <Typography variant="body2">
              Fill in your company's legal name, EIN, mailing address, and entity type (LLC, S-Corp, C-Corp, etc.).
            </Typography>
            <SectionHeading label="Where" />
            <Typography variant="body2">
              Navigate to the <strong>Company Setup</strong> tab.
            </Typography>
            <SectionHeading label="Why this matters" />
            <Typography variant="body2">
              This information is required for generating pay stubs, W-2 forms, and quarterly/annual tax filings (941, 940, and for Pennsylvania the UC-2, PA-W3, REV-1667 and local EIT/LST worksheets).
              Without accurate company information, payroll documents will be incomplete.
            </Typography>
            <SectionHeading label="Key fields" />
            <BulletList
              items={[
                'Legal company name (display_name)',
                'Employer Identification Number (EIN)',
                'Business address (street, city, state, ZIP)',
                'Entity type (LLC, S-Corp, C-Corp, Sole Proprietor)',
                'State Tax ID: Pennsylvania withholding account number (used by the PA-W3, REV-1667 and W-2 Box 15 for PA employees)',
                'State Unemployment ID (SUI): your PA UC employer account number (printed on the UC-2 worksheet)',
                'SUTA Rate (%) and SUTA Wage Base ($): your total employer unemployment contribution rate from the state rate notice (Pennsylvania: UC-657) and the taxable wage base (Pennsylvania $10,000). Used for the employer unemployment tax on each paycheck and for the Pennsylvania UC-2 worksheet',
              ]}
            />
          </Box>
        );

      case 'employees':
        return (
          <Box>
            <SectionHeading label="What to do" />
            <Typography variant="body2">
              Add all employees with their personal information, pay configuration, and tax filing details.
            </Typography>
            <SectionHeading label="Where" />
            <Typography variant="body2">
              Navigate to the <strong>People</strong> tab, then click <strong>Add Person</strong>.
            </Typography>
            <SectionHeading label="Key fields" />
            <BulletList
              items={[
                'Full name, email, phone',
                'Hire Date (required)',
                'SSN (last 4 digits) - required for W-2 generation',
                'Home address (Address section: Street Address, City, ZIP Code) - printed on the W-2',
                'Employee type: Hourly or Salary',
                'For hourly employees: hourly_rate, overtime multiplier',
                'For salary employees: salary_amount, pay_frequency',
                'Filing status (Single, Married Filing Jointly, Head of Household)',
                'State of residence (for state tax withholding)',
                'W-4 details (Edit > Tax / W-4, after the person is added): Filing Status, Step 2: Multiple Jobs, Dependent Credits, Other Income, Deductions, Extra Withholding, W-4 Signed Date, W-4 Received Date and W-4 Basis; IRS lock-in letters and the notices that modify or release them are recorded there too',
                'Pennsylvania local tax (Edit > Tax / W-4 > Local Tax (PA Municipalities), after the person is added): Resident PSD Code, Work Location PSD Code (for someone who works from home all or most of the time, enter the home PSD code; if they split time between an office and home, ask the tax collector which worksite to report), EIT/LST Collector, Local EIT Rate and Local LST (Annual)',
              ]}
            />
            <Alert severity="info" sx={{ mt: 1.5 }}>
              Salary employees need both <strong>salary_amount</strong> and <strong>pay_frequency</strong> set.
              The system derives the per-period pay from these fields. Hourly employees need <strong>hourly_rate</strong> and
              optionally overtime settings. Overtime starts after 40 hours a week under the FLSA: an <strong>OT Threshold</strong> above 40
              needs an <strong>FLSA Exception</strong> and reason (the § 7(j) 8/80 hospital system is not supported), and turning overtime off is recorded in the audit log.
              {' '}<strong>OT Multiplier</strong> must be at least 1.5 while overtime is on. Set <strong>Work State</strong> when
              someone works in another state: HourTrax warns about state exempt-salary floors and daily overtime (for example California,
              Alaska, Nevada and Colorado; the list is not complete) but computes weekly overtime only.
            </Alert>
          </Box>
        );

      case 'companies':
        return (
          <Box>
            <SectionHeading label="What to do" />
            <Typography variant="body2">
              Add the client companies your employees work for. These are the businesses you bill for employee time.
            </Typography>
            <SectionHeading label="Where" />
            <Typography variant="body2">
              Navigate to the <strong>{t('client.plural')}</strong> tab, then click <strong>Add {t('client.singular')}</strong>.
            </Typography>
            <SectionHeading label="Why this matters" />
            <Typography variant="body2">
              Time entries and invoices are grouped by client company. Each employee's hours are tracked against a specific client,
              and invoices are generated per-client.
            </Typography>
            <SectionHeading label="Optional settings" />
            <BulletList
              items={[
                'Default invoice format: Standard PDF or FERC Excel',
                'Company contact information for invoice delivery',
                'Bill rate and pay rate overrides per employee and client (Client Bill Rates)',
              ]}
            />
          </Box>
        );

      case 'payperiods':
        return (
          <Box>
            <SectionHeading label="What to do" />
            <Typography variant="body2">
              Configure your pay period schedule to define when employees get paid.
            </Typography>
            <SectionHeading label="Where" />
            <Typography variant="body2">
              Navigate to the <strong>Pay Periods</strong> tab.
            </Typography>
            <SectionHeading label="Steps" />
            <BulletList
              items={[
                'Choose a frequency: weekly, biweekly, semimonthly, or monthly',
                'Create a pay period preset with start date and frequency',
                'Click Generate Periods (Generate on a phone), enter the Number of Periods (1 to 120 per run) and, optionally, a Starting Date (leave it blank to start from today), then click Generate - HourTrax creates the future periods from your settings and skips any that already exist',
                'Lock a pay period before running payroll for that period',
              ]}
            />
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Pay periods must be <strong>locked</strong> before you can create a payroll run for them.
              Locking prevents time entry changes during payroll processing.
            </Alert>
          </Box>
        );

      case 'deductions':
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              This step is optional. You can set up deductions later or skip entirely if not needed.
            </Alert>
            <SectionHeading label="What to do" />
            <Typography variant="body2">
              Create deduction templates for benefits like health insurance, retirement plans, and other withholdings.
              Then assign templates to individual employees.
            </Typography>
            <SectionHeading label="Where" />
            <Typography variant="body2">
              Navigate to the <strong>Deductions</strong> tab. Create templates first, then assign them to employees.
            </Typography>
            <SectionHeading label="Deduction categories" />
            <BulletList
              items={[
                'Health insurance',
                'Dental insurance',
                'Vision insurance',
                'Life insurance (the Employer Amount is group-term life imputed income: Social Security and Medicare apply, FUTA and state unemployment do not)',
                '401(k) retirement',
                'SIMPLE IRA',
                'HSA (Health Savings Account)',
                'FSA (Flexible Spending Account)',
                'Other custom deductions',
              ]}
            />
            <SectionHeading label="Pre-tax vs. post-tax" />
            <Typography variant="body2">
              <strong>Pre-tax deductions</strong> (Section 125 cafeteria plans) reduce taxable income before federal and state
              tax calculations. This includes health, dental, vision, HSA, and FSA. <strong>Post-tax deductions</strong> are
              taken after taxes are calculated (e.g., Roth 401k, garnishments). Life and disability insurance marked Pre-Tax are
              treated as Section 125 too (they also reduce Pennsylvania income tax and local EIT); use Pre-Tax for them only through a cafeteria plan,
              and for life insurance only for group-term life. Pennsylvania unemployment (PA UC) is the exception: it is figured
              on gross pay before pre-tax deductions, less only group-term life imputed income. Spouse and dependent life insurance cannot be Pre-Tax: set it to Post-Tax (outside the cafeteria plan). 401(k) and SIMPLE IRA deferrals reduce federal income tax but not Social Security, Medicare, Pennsylvania income tax or local EIT, so a Pennsylvania employee&apos;s W-2 Box 16 (state wages) includes them.
            </Typography>
            <SectionHeading label="Employer contributions" />
            <Typography variant="body2">
              You can configure employer matching (e.g., 50% match on 401k up to 6% of salary) or flat employer contributions
              per deduction template.
            </Typography>
          </Box>
        );

      case 'timetracking':
        return (
          <Box>
            <SectionHeading label="What to do" />
            <Typography variant="body2">
              Employees log their hours against client companies. Time can be entered manually or via the clock in/out feature.
            </Typography>
            <SectionHeading label="Where" />
            <Typography variant="body2">
              Employees use the <strong>My Time Entries</strong> tab. Admins and managers review hours on the{' '}
              <strong>Timesheets</strong> tab.
            </Typography>
            <SectionHeading label="Approval flow" />
            <BulletList
              items={[
                'Employee enters time (hours worked, date, client company)',
                'Manager reviews and approves the entry',
                'Approved entries become eligible for invoicing or payroll',
              ]}
            />
            <SectionHeading label="Entry types" />
            <BulletList
              items={[
                'Regular: Standard hours worked (tracked in hours)',
                'Bonus: One-time bonus payments (tracked as dollar amount)',
                'Time Off: PTO/sick days (linked to PTO approval system)',
              ]}
            />
          </Box>
        );

      case 'modules':
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              This step is optional. Everything is on by default — only visit it if your organization doesn't use
              benefits, time off or payroll, or you want employees to land on just the clock.
            </Alert>
            <SectionHeading label="What to do" />
            <Typography variant="body2">
              Switch off the modules you don't use and choose what employees see when they open HourTrax.
            </Typography>
            <SectionHeading label="Where" />
            <Typography variant="body2">
              Navigate to the <strong>Company Setup</strong> tab, open the <strong>General</strong> tab, then find the{' '}
              <strong>Modules</strong> section. It only appears for users with the <strong>Configure settings</strong> permission.
            </Typography>
            <SectionHeading label="Settings" />
            <BulletList
              items={[
                'Benefits: benefit plans, enrollment periods and the My Benefits page',
                'Time off: time-off requests, PTO balances and PTO approvals — balances keep accruing while it is off. Cannot be turned off while time-off requests are pending',
                'Payroll: payroll runs, deductions, tax filing, pay stubs and the My Pay page — turn off if you pay people outside HourTrax (for example contractors paid by check) — PTO keeps accruing either way, credited when each pay period ends (per-pay-period employees only while Payroll is off). Existing payroll records, pay stubs and tax filings stay readable: with past payroll, My Pay and the Payroll and Tax Filing tabs stay visible read-only. Cannot be turned off while a payroll run is open',
                'Employee home screen: Full dashboard (default) or Clock only — Clock only gives employees just the time clock, with the manual entry form behind a "Missed a punch? Add time" button that only shows while they are clocked out',
                'Each switch saves immediately and the menu updates for everyone right away — no Save Company Settings needed',
                'Terminology (card below Modules): rename Client, Affiliate, Department and Property to your own words — for example LLC and Property — then click Save terminology',
                'Started from: if HourTrax set up your organization from a starting profile (for example Field crew / property), the Modules section names it. It is only a record — every switch still works on its own',
              ]}
            />
            <SectionHeading label="Contractors (1099)" />
            <Typography variant="body2">
              Set a person's <strong>Employee Type</strong> to <strong>Contractor (1099)</strong> under <strong>Compensation</strong> on
              the People screen. Contractors never see Time Off, My Pay or My Benefits, and payroll runs skip them — no withholding,
              no pay stub. Their hours are still totaled at their Hourly Rate.
            </Typography>
          </Box>
        );

      case 'pipeline':
        return (
          <Box>
            <SectionHeading label="What to do" />
            <Typography variant="body2">
              Understand the full payment lifecycle from time entry to employee payment. The Pipeline tab shows entries
              at every stage.
            </Typography>
            <SectionHeading label="Where" />
            <Typography variant="body2">
              Navigate to the <strong>Pipeline</strong> tab to see all entries grouped by status.
            </Typography>
            <SectionHeading label="Status flow" />
            <List dense disablePadding>
              {[
                { label: 'Pending', desc: 'Employee submits time entry' },
                { label: 'Approved', desc: 'Manager approves the entry' },
                { label: 'Invoiced', desc: 'Admin creates an invoice for the client (Invoices tab)' },
                { label: 'Invoice Paid', desc: 'Client pays the invoice - entries move to payroll-ready' },
                { label: 'Payroll Ready', desc: 'Entry is eligible for the next payroll run' },
                { label: 'Paid', desc: 'Payroll processed, employee has been paid' },
              ].map((item, i) => (
                <ListItem key={i} sx={{ py: 0.25, pl: 2 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {i + 1}.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        <strong>{item.label}</strong> — {item.desc}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
            <Alert severity="info" sx={{ mt: 1.5 }}>
              For internal employees (not billed to a client), entries can be moved directly from Approved to Payroll Ready,
              skipping the invoicing steps.
            </Alert>
          </Box>
        );

      case 'payroll':
        return (
          <Box>
            <SectionHeading label="What to do" />
            <Typography variant="body2">
              Process payroll to calculate taxes, deductions, and net pay for your employees.
            </Typography>
            <SectionHeading label="Where" />
            <Typography variant="body2">
              Navigate to the <strong>Payroll</strong> tab.
            </Typography>
            <SectionHeading label="Steps to run payroll" />
            <List dense disablePadding>
              {[
                'Lock the pay period (Pay Periods tab) to prevent time entry changes',
                'Create a payroll run for the locked period',
                'System auto-calculates: gross pay, federal withholding, state tax, FICA (Social Security + Medicare), deductions, net pay',
                'Review all calculations for accuracy',
                'Approve the payroll run',
                'Process the run — this marks entries as paid, credits PTO accrual (hours used came off the balance at approval), and generates pay stubs',
              ].map((item, i) => (
                <ListItem key={i} sx={{ py: 0.25, pl: 2 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {i + 1}.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
            <SectionHeading label="Taxes calculated" />
            <BulletList
              items={[
                'Federal income tax withholding (2026 Pub 15-T percentage method)',
                'Social Security (6.2% up to wage base)',
                'Medicare (1.45%, plus 0.9% additional Medicare over $200k)',
                'State income tax (varies by state)',
                'Local taxes: EIT and LST for Pennsylvania employees',
              ]}
            />
            <SectionHeading label="Pay stubs" />
            <Typography variant="body2">
              After processing, pay stubs are available under <strong>Payroll</strong> {'>'} View the completed run {'>'}{' '}
              <strong>Download Stubs</strong>. Employees can also download their own stubs. To email someone their stubs or a copy of their W-2 (for example after they leave), use <strong>Send pay records</strong> on their row on the <strong>People</strong> tab.
            </Typography>
            <SectionHeading label="W-2 delivery" />
            <Typography variant="body2">
              Employees opt in to electronic W-2s themselves under <strong>My Pay</strong> {'>'} <strong>Tax Documents (W-2)</strong> — the IRS requires their consent, and paper is the default.
              At year-end, generate W-2s on the <strong>Tax Filing</strong> tab, then scroll to <strong>W-2 Delivery</strong> and click <strong>Release</strong>:
              while electronic delivery is off (it stays off until W-2 record keeping for electronic delivery is in place) Release posts a courtesy PDF copy for consenting employees, but
              everyone released, consenting employees included, stays on the <strong>Paper required</strong> list and needs the official printed W-2 by January 31. The W-2 PDF from Tax Filing is that printed W-2 once its Review chip lists no missing items (a chip that shows only the Box 10 FSA note or the instruction-edition note is advisory: check it, then furnish): fill in Company Setup > Tax Identification (EIN, State Tax ID) and Address & Contact first.
              Once the tenant setting <strong>w2_electronic_furnishing_enabled</strong> is turned on, consenting employees are notified and can download the official PDF and only the rest need paper.
              Email must be enabled for the notice to go out by email. If an employee asks in writing to stop electronic delivery, click <strong>Record written withdrawal</strong> on their row and enter a note.
            </Typography>
            <SectionHeading label="Tax deposits" />
            <Typography variant="body2">
              HourTrax does not pay payroll taxes. On the <strong>Tax Deposits</strong> tab, open <strong>Deposit settings</strong> and set{' '}
              <strong>Federal deposit schedule</strong> (from your IRS lookback period; Monthly for a new employer) and{' '}
              <strong>PA withholding frequency</strong> (as assigned by PA Department of Revenue), then click <strong>Save settings</strong>.
              The tab then lists each deposit with its due date. After paying on EFTPS, myPATH, the UC system or your local collector,
              click <strong>Record payment</strong>. Turn on <strong>Email reminders</strong> to be reminded before each due date.
            </Typography>
          </Box>
        );

      case 'invoiceautomation':
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              This step is optional. You can always create invoices manually from the Invoices tab.
            </Alert>
            <SectionHeading label="What to do" />
            <Typography variant="body2">
              Set up automatic invoice creation from approved time entries on a recurring schedule.
            </Typography>
            <SectionHeading label="Where" />
            <Typography variant="body2">
              Navigate to the <strong>Company Setup</strong> tab, then find the <strong>Invoice Automation</strong> section.
            </Typography>
            <SectionHeading label="Settings" />
            <BulletList
              items={[
                'Schedule: weekly, biweekly, or monthly invoice generation',
                'Auto-push to Zoho: If connected, invoices are automatically sent to Zoho Invoice',
                'Manual trigger: Use the "Run Now" button to generate invoices on demand',
              ]}
            />
          </Box>
        );

      case 'payrollautomation':
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              This step is optional. You can always run payroll manually from the Payroll tab.
            </Alert>
            <SectionHeading label="What to do" />
            <Typography variant="body2">
              Configure automatic payroll runs that trigger when pay periods are locked and eligible entries exist.
            </Typography>
            <SectionHeading label="Where" />
            <Typography variant="body2">
              Navigate to the <strong>Company Setup</strong> tab, then find the <strong>Payroll Automation</strong> section.
            </Typography>
            <SectionHeading label="Available settings" />
            <BulletList
              items={[
                'Enable automatic payroll: Checks every 60 seconds between 6-9 AM for locked periods with payroll-ready entries or salaried employees, then creates and calculates the run. HourTrax cannot tell when you pay, so an automatic run has no Paycheck Date and stops in review. Everyone who can run payroll gets a "Payroll run needs a Paycheck Date" notification. Open the run on the Payroll tab, set the Paycheck Date with the pencil icon next to Paycheck, click Select Entries & Calculate if the run goes back to draft, then click Approve.',
                'Require manager approval: Sends email with a secure review link. Managers can review each employee breakdown, then approve or reject. Approval tokens expire after 48 hours. No email is sent for a run without a Paycheck Date, so automatic runs are approved on the Payroll tab instead.',
                'Auto-process on check date: Automatically processes approved payroll runs when the check date arrives. If a run is refused or fails, everyone who can run payroll is notified once a day and Payroll Automation shows "Last automatic run problem" (info icon) until the run is processed or voided; the notice can be switched off under "Payroll Automation Failed" in notification preferences.',
                'Auto-generate pay stubs: Creates PDF pay stubs immediately upon payroll processing',
              ]}
            />
            <SectionHeading label="Automated flow" />
            <List dense disablePadding>
              {[
                'Locked pay period detected with eligible entries',
                'System auto-calculates the payroll run and leaves it in review',
                'You set the Paycheck Date on the Payroll tab and approve the run',
                'Run auto-processes on the check date (if Auto-process on check date is on)',
                'Pay stubs generated automatically',
              ].map((item, i) => (
                <ListItem key={i} sx={{ py: 0.25, pl: 2 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {i + 1}.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText
                    primary={item}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        );

      case 'worksites':
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              This step is optional. Work sites are only needed if you want to restrict clock-in/out or time entry creation to specific networks, or set up kiosk devices.
            </Alert>
            <SectionHeading label="What are work sites?" />
            <Typography variant="body2">
              Work sites represent physical locations where employees work. Each site can have IP restrictions (to ensure employees are on-site),
              geofence coordinates (for a future mobile app), and kiosk devices for shared timeclocks.
            </Typography>
            <SectionHeading label="Where" />
            <Typography variant="body2">
              Navigate to <strong>Settings {'>'} Work Sites</strong> tab.
            </Typography>
            <SectionHeading label="Setting up a work site" />
            <List dense disablePadding>
              {[
                'Click "Add Work Site" and enter the site name and address',
                'Set the default company — kiosk clock-ins at this site will use this company',
                'Enable IP enforcement if you want to restrict access by network',
                'Choose enforcement mode: "Hard Block" prevents clock-in entirely, "Soft Flag" allows but flags it for review',
                'Optionally add geofence coordinates (latitude, longitude, radius) for future mobile app use',
                'Enable kiosk mode if you plan to set up a shared timeclock device at this site',
              ].map((item, i) => (
                <ListItem key={i} sx={{ py: 0.25, pl: 2 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {i + 1}.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
            <SectionHeading label="IP whitelist" />
            <Typography variant="body2">
              Click on a work site to open its detail panel, then go to the <strong>IP Whitelist</strong> tab.
              Add the public IP addresses or CIDR ranges that employees should be connecting from.
              For example, your office's public IP (e.g., <code>71.64.114.118</code>) or an entire subnet (e.g., <code>10.20.20.0/24</code>).
            </Typography>
            <SectionHeading label="Assigning employees" />
            <Typography variant="body2">
              Go to the <strong>Employees</strong> tab within the work site detail panel. Assign employees who work at this location.
              Managers who don't have the View all employees permission see only their own direct reports in this list. They can also assign, remove and set kiosk PINs only for those employees.
              When IP restriction for entry creation is enabled (see Shift Config), only assigned employees with matching IPs can create time entries.
            </Typography>
            <SectionHeading label="On-site entry restriction" />
            <Typography variant="body2">
              To require employees to be on-site to create any time entries (not just kiosk), go to <strong>Settings {'>'} Shift Config</strong> and
              enable <strong>"Require on-site network for new time entries"</strong>. This checks the employee's IP against their assigned work site's
              IP rules when they try to create a manual time entry or clock in via the web. Admins and managers are not restricted.
            </Typography>
          </Box>
        );

      case 'kiosk':
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              This step is optional. The kiosk is for shared devices (tablets) where multiple employees clock in and out using a PIN.
            </Alert>
            <SectionHeading label="What is kiosk mode?" />
            <Typography variant="body2">
              Kiosk mode turns a tablet or computer into a shared timeclock. Employees identify themselves with a 4-digit PIN,
              then clock in or out with one tap. The device stays on a full-screen clock display between uses. No login or navigation is needed.
            </Typography>
            <SectionHeading label="Setting up a kiosk" />
            <List dense disablePadding>
              {[
                'Create a work site (Settings > Work Sites) and enable "Kiosk mode" in the site settings',
                'Set the default company for the site — this is the company kiosk clock-ins will be recorded under',
                'Optionally enable "Require photo on clock-in/out" for anti-fraud photo capture',
                'Click on the site, go to the Kiosk Devices tab, and click "Register" to create a new device',
                'A QR code will appear — scan it with the tablet camera to open the kiosk URL directly',
                'Alternatively, copy the URL and open it in the tablet\'s browser, then bookmark it',
                'Set the browser to full-screen mode for a clean kiosk experience',
              ].map((item, i) => (
                <ListItem key={i} sx={{ py: 0.25, pl: 2 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {i + 1}.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
            <SectionHeading label="Setting up employee PINs" />
            <Typography variant="body2">
              Click on the work site, go to the <strong>Employees</strong> tab, assign employees to the site, then click
              <strong> Set PIN</strong> for each employee. PINs must be exactly 4 digits and unique within your organization.
              You can use the "Generate Random PIN" button or enter one manually. Distribute PINs to employees securely.
            </Typography>
            <SectionHeading label="How employees use the kiosk" />
            <List dense disablePadding>
              {[
                'Employee approaches the kiosk and enters their 4-digit PIN on the numeric keypad',
                'The screen shows their name and current status (clocked in or not)',
                'If a photo is required, the camera preview activates',
                'Employee taps "Clock In" or "Clock Out"',
                'A success message appears, then the screen resets to the idle state after a few seconds',
              ].map((item, i) => (
                <ListItem key={i} sx={{ py: 0.25, pl: 2 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {i + 1}.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText primary={item} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
            <SectionHeading label="IP enforcement on kiosk" />
            <Typography variant="body2">
              If IP enforcement is enabled on the work site, the kiosk will only load when accessed from an authorized network.
              If someone tries to open the kiosk URL from an unauthorized network, they will see an error message. This prevents
              employees from accessing the kiosk remotely.
            </Typography>
            <SectionHeading label="Audit trail" />
            <Typography variant="body2">
              Every kiosk clock-in and clock-out is recorded in the punch events audit log with the employee's IP address,
              device information, verification method (PIN or PIN + photo), and timestamp. This data is available in the
              Transaction Log for compliance and dispute resolution.
            </Typography>
          </Box>
        );

      case 'permissions':
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 1.5 }}>
              This step is optional. Default permissions are pre-configured for each role.
            </Alert>
            <SectionHeading label="Time editing permission" />
            <Typography variant="body2">
              The <strong>Edit entry times</strong> permission (<code>can_edit_entry_times</code>) controls whether a user can modify
              the start time, end time, date, and break minutes on time entries. Without this permission, employees can still edit
              notes, company, affiliate, and billable status on their entries — but the time fields are locked and grayed out.
            </Typography>
            <SectionHeading label="Default settings by role" />
            <BulletList
              items={[
                'Admin: Can edit times (has all permissions)',
                'Payroll Admin: Can edit times',
                'Manager: Can edit times (for their managed employees)',
                'Employee: Cannot edit times — sees "Contact a manager to update times on your time entry"',
              ]}
            />
            <SectionHeading label="Why restrict time editing?" />
            <Typography variant="body2">
              Preventing employees from modifying their own clock times reduces the risk of time theft and ensures accurate payroll.
              If an employee clocked in or out at the wrong time, they ask their manager to make the correction — creating an audit trail
              of who changed what.
            </Typography>
            <SectionHeading label="Work site management permission" />
            <Typography variant="body2">
              The <strong>Manage work sites & kiosks</strong> permission (<code>can_manage_work_sites</code>) controls who can create
              and edit work sites, IP restrictions, kiosk devices, and employee site assignments. By default, this is enabled for
              Admin and Payroll Admin roles.
            </Typography>
            <SectionHeading label="Customizing permissions" />
            <Typography variant="body2">
              Go to <strong>Settings {'>'} Roles</strong> to create custom roles with specific permission combinations. For example,
              you could create a "Site Manager" role that has <code>can_manage_work_sites</code> and <code>can_approve_time</code> but
              not <code>can_run_payroll</code>. A manager on the preset Manager role gets exactly that role's permissions — to restrict one manager, create a custom role and pick it in the person's Role field on Settings {'>'} People. A custom role picked there is stored on the login as that role; underneath, the login counts as an Employee, so the role grants exactly the permissions you ticked and nothing by role name. Per-user permission switches in the Manage Employees dialog appear only for older manager logins that have no role assigned.
            </Typography>
            <SectionHeading label="Direct reports" />
            <Typography variant="body2">
              On <strong>Settings {'>'} People</strong>, the <strong>Employees</strong> button (the <strong>Manage Employees</strong> icon on a wide screen) appears for every Manager login and for any login whose role includes a team permission — <strong>Approve time entries</strong>, <strong>Approve PTO requests</strong>, <strong>Approve expenses</strong>, <strong>Manage work sites &amp; kiosks</strong> or <strong>Manage tasks</strong> (Payroll Admins included). Pick the people they supervise under <strong>Employees to Manage</strong> and click <strong>Save</strong>; the dialog shows which of their permissions made it appear. Those permissions then apply only to the selected employees, and changes take effect right away. A role with none of these permissions can't be given direct reports — add one on <strong>Settings {'>'} Roles</strong> first. Admins see and decide everything already, so the button isn't shown for them.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: 2 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Documentation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Setup guides, feature documentation, and workflow references for HourTrax. Required initial setup steps
          are tracked with progress indicators — optional features can be configured at any time.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <LinearProgress
              variant={loading ? 'indeterminate' : 'determinate'}
              value={progressPercent}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120, textAlign: 'right' }}>
            {loading ? 'Loading...' : `${completedRequired} of ${REQUIRED_STEP_COUNT} setup steps complete`}
          </Typography>
        </Box>
      </Paper>

      {STEPS.map((step) => {
        const StepIcon = step.icon;
        const status = getEffectiveStatus(step.id);
        const autoDetected = stepStatuses[step.id] === 'complete';

        return (
          <Accordion
            key={step.id}
            expanded={expanded === step.id}
            onChange={handleAccordionChange(step.id)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 } }}
            >
              <StatusChip status={status} />
              <StepIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                {step.title}
              </Typography>
              {!step.required && (
                <Chip label="Optional" size="small" variant="outlined" sx={{ mr: 1 }} />
              )}
            </AccordionSummary>
            <AccordionDetails>
              <Divider sx={{ mb: 1.5 }} />
              {renderStepContent(step.id)}
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                {autoDetected ? (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Automatically detected as complete"
                    color="success"
                    size="small"
                    variant="outlined"
                  />
                ) : status === 'complete' ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<RadioButtonUncheckedIcon />}
                    onClick={() => toggleManualComplete(step.id)}
                  >
                    Mark as Incomplete
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => toggleManualComplete(step.id)}
                  >
                    Mark as Complete
                  </Button>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Need help? Contact your HourTrax administrator or refer to the documentation for detailed instructions
          on each feature.
        </Typography>
        <Button
          variant="outlined"
          size="small"
          sx={{ mt: 1 }}
          onClick={fetchStatus}
          disabled={loading}
        >
          Refresh Status
        </Button>
      </Paper>
    </Box>
  );
}
